import React, { useEffect, useState, useRef } from "react";
import { useParams } from "wouter";
import MuxPlayer from "@mux/mux-player-react";
import videos from "@/content/videos.json";
import { Link } from "wouter";
import ResponsiveImage from "@/components/responsive-image";

interface VideoData {
  id: string;
  title: string;
  muxPlaybackId: string;
}

export default function VideoPage() {
  const params = useParams<{ videoId: string }>();
  const [videoData, setVideoData] = useState<VideoData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const playerRef = useRef<any>(null);

  useEffect(() => {
    setLoading(true);
    
    // Find the video with the matching ID
    const foundVideo = videos.videos.find(video => video.id === params.videoId);
    
    if (foundVideo) {
      setVideoData(foundVideo);
      setError(null);
      // Set document title
      document.title = `${foundVideo.title} | 28X`;
    } else {
      setVideoData(null);
      setError("Video not found");
      document.title = "Video Not Found | 28X";
    }
    
    setLoading(false);
  }, [params.videoId]);

  // Effect to ensure video autoplays
  useEffect(() => {
    if (playerRef.current && videoData) {
      // Try to play the video after a short delay to ensure DOM is ready
      const playPromise = setTimeout(() => {
        const playerElement = playerRef.current;
        if (playerElement) {
          // Attempt to play - this will return a promise
          const playAttempt = playerElement.play();
          
          if (playAttempt !== undefined) {
            playAttempt.catch((error: Error) => {
              // Auto-play was prevented - this is expected in some browsers
              console.log('Autoplay prevented:', error);
              // We could show a play button overlay here if needed
            });
          }
        }
      }, 500);
      
      return () => clearTimeout(playPromise);
    }
  }, [videoData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#301c36] flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (error || !videoData) {
    return (
      <div className="min-h-screen bg-[#301c36] flex flex-col items-center justify-center p-6">
        <div className="text-white text-2xl mb-6">{error}</div>
        <Link href="/">
          <div className="px-6 py-3 bg-[#dd2c67] text-white rounded-md hover:bg-[#c1265a] cursor-pointer">
            Return to Home
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#301c36] flex flex-col">
      <header className="w-full p-4 md:p-6">
        <Link href="/">
          <div className="inline-block">
            <ResponsiveImage
              src="/assets/upper-left-28x-logo.svg"
              alt="28X Logo"
              className="w-[70px] md:w-[99px]"
              priority={true}
              lazy={false}
            />
          </div>
        </Link>
      </header>
      
      <main className="flex-1 flex flex-col items-center justify-center p-4 md:p-6">
        
        <div className="w-full max-w-4xl mx-auto aspect-video bg-black rounded-lg overflow-hidden">
          <MuxPlayer
            ref={playerRef}
            streamType="on-demand"
            playbackId={videoData.muxPlaybackId}
            autoPlay={true}
            loop={false}
            preload="auto"
            muted={false}
            metadata={{
              video_title: videoData.title,
              viewer_user_id: "anonymous",
            }}
            className="w-full h-full"
          />
        </div>
      </main>
      
      <footer className="w-full p-4 md:p-6 text-center text-[#f4eadf] text-sm">
        <p>&copy; {new Date().getFullYear()} 28X LTD. All rights reserved.</p>
      </footer>
    </div>
  );
}
